import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchOrganizations, fetchPlatformLicenses } from "@/lib/platformApi";
import { usePlatformDesigner } from "@/lib/usePlatformDesigner";
import {
  buildBuyerPreviewSystemInfoFromOrganization,
  buildEmptyBuyerPreviewSimulationSystemInfo,
  readPlatformDesignerBuyerPreviewOrgId,
  writePlatformDesignerBuyerPreviewOrgId,
} from "@/lib/platformDesignerBuyerPreview";
import { PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT } from "@/lib/usePlatformDesignerBuyerPreview";
import { usePlatformDesignerBuyerPreview } from "@/lib/usePlatformDesignerBuyerPreview";

export function usePlatformDesignerBuyerPreviewOrg({ baseSystemInfo = null } = {}) {
  const isPlatformDesigner = usePlatformDesigner();
  const buyerPreviewActive = usePlatformDesignerBuyerPreview();
  const enabled = Boolean(isPlatformDesigner && buyerPreviewActive);

  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgId, setOrgIdState] = useState(() => readPlatformDesignerBuyerPreviewOrgId());
  const [orgLicense, setOrgLicense] = useState(null);

  const loadOrganizations = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const items = await fetchOrganizations();
      setOrganizations(Array.isArray(items) ? items : []);
    } catch {
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    const sync = () => setOrgIdState(readPlatformDesignerBuyerPreviewOrgId());
    window.addEventListener(PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT, sync);
    return () => window.removeEventListener(PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT, sync);
  }, []);

  const selectedOrg = useMemo(
    () => organizations.find((o) => String(o.id) === String(orgId)) || null,
    [organizations, orgId],
  );

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !orgId) {
      setOrgLicense(null);
      return undefined;
    }
    fetchPlatformLicenses(orgId)
      .then((items) => {
        if (cancelled) return;
        const list = Array.isArray(items) ? items : [];
        const current =
          list.find((l) => l.is_current && l.status === "active")
          || list.find((l) => l.status === "active")
          || null;
        setOrgLicense(current);
      })
      .catch(() => {
        if (!cancelled) setOrgLicense(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, orgId]);

  const setOrgId = useCallback((nextId) => {
    const id = String(nextId || "").trim();
    writePlatformDesignerBuyerPreviewOrgId(id);
    setOrgIdState(id);
    window.dispatchEvent(new CustomEvent(PLATFORM_DESIGNER_BUYER_PREVIEW_EVENT, { detail: { orgId: id } }));
  }, []);

  const previewSystemInfo = useMemo(() => {
    if (!enabled) return null;
    if (!selectedOrg) return buildEmptyBuyerPreviewSimulationSystemInfo();
    return buildBuyerPreviewSystemInfoFromOrganization(selectedOrg, orgLicense, baseSystemInfo || {});
  }, [enabled, selectedOrg, orgLicense, baseSystemInfo]);

  return {
    enabled,
    organizations,
    loading,
    orgId,
    setOrgId,
    selectedOrg,
    orgLicense,
    hasOrganizationSelected: Boolean(selectedOrg),
    previewSystemInfo,
    reloadOrganizations: loadOrganizations,
  };
}
